import React, { useEffect, useMemo, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocation, useMatch } from 'react-router-dom';
import qs from 'query-string';

import { tunnelSelectors, userSelectors } from 'reducers';
import { fundTypes, queryStringifyOptions } from 'utils/constants';
import { useDistributorShareCategories } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import { RoleSelect } from './RoleSelect';
import FundSelect from './FundSelect';
import FCPRPedagogicalStep from './FCPRPedagogicalStep';
import { ShareCategoryDialog } from 'components';
import { Typography } from '@mui/material';

const TunnelEntry = ({
    fundName,
    fundSubmitted,
    setFundSubmitted,
    roleChoice,
    setRoleSubmitted,
    roleSubmitted,
    submittedPedagogicalStep,
    setSubmittedPedagogicalStep,
    internalUser,
}) => {
    const location = useLocation();
    const hasFinishedSubscription = useSelector(userSelectors.getFinishedSubscriptionStatus);
    const isIndirect = !!useMatch('/create-subscription/*');
    const currentInvestmentFund = useSelector(tunnelSelectors.getCurrentInvestmentFund);
    const shareSubcategoryId = useSelector(userSelectors.getShareSubcategoryId);

    const [shareCategoryDialogOpen, setShareCategoryDialogOpen] = useState(false);

    const isFCPR = currentInvestmentFund?.type === fundTypes.FCPR;

    const userId = useMemo(() => {
        const parsedQuery = qs.parse(location.search, queryStringifyOptions);
        return parsedQuery?.userId || null;
    }, [location]);

    const [distributorCategories] = useDistributorShareCategories(currentInvestmentFund?.id);

    useEffect(() => {
        if (
            isIndirect &&
            fundSubmitted &&
            !userId &&
            !shareSubcategoryId &&
            distributorCategories.length > 1
        ) {
            // when creating an indirect subscription from the homepage
            // the investment fund is already determined and <FundSelect /> will not render
            setShareCategoryDialogOpen(true);
        }
    }, [
        fundSubmitted,
        isIndirect,
        shareSubcategoryId,
        userId,
        internalUser,
        distributorCategories,
    ]);

    return (
        <>
            <FundSelect
                fundSubmitted={fundSubmitted}
                setFundSubmitted={setFundSubmitted}
                isIndirect={isIndirect}
                hasFinishedSubscription={hasFinishedSubscription}
            />
            {!roleSubmitted && fundSubmitted && (
                <RoleSelect
                    roleChoice={roleChoice}
                    isIndirect={isIndirect}
                    setRoleSubmitted={setRoleSubmitted}
                    title={texts.SELECTIONNEZ_VOTRE_PROFIL}
                    subtitle={
                        <React.Fragment>
                            <Typography variant="subtitle1">
                                {texts.ROLE_SELECT_SUBTITLE}
                            </Typography>
                            <Typography variant="subtitle1">{texts.SELECTIONNEZ_PROFIL}</Typography>
                        </React.Fragment>
                    }
                />
            )}
            {isFCPR && roleSubmitted && !submittedPedagogicalStep && (
                <FCPRPedagogicalStep
                    setSubmittedPedagogicalStep={setSubmittedPedagogicalStep}
                    fundName={fundName}
                    roleChoice={roleChoice}
                />
            )}
            <ShareCategoryDialog
                open={shareCategoryDialogOpen}
                onClose={() => setShareCategoryDialogOpen(false)}
                onConfirm={() => setShareCategoryDialogOpen(false)}
            />
        </>
    );
};

TunnelEntry.propTypes = {
    fundName: PropTypes.string.isRequired,
    fundSubmitted: PropTypes.bool,
    setFundSubmitted: PropTypes.func.isRequired,
    roleChoice: PropTypes.string,
    setRoleSubmitted: PropTypes.func.isRequired,
    roleSubmitted: PropTypes.bool,
    submittedPedagogicalStep: PropTypes.bool,
    setSubmittedPedagogicalStep: PropTypes.func.isRequired,
    internalUser: PropTypes.object,
};

export default TunnelEntry;
